import React, { useEffect, useState } from 'react';
import {
    CheckCircle2,
    XCircle,
    Rocket,
    Target,
    TrendingUp,
    Sparkles,
    BarChart3,
    HeartHandshake,
    Calendar,
    Workflow,
    Eye,
    ShoppingBag,
    IndianRupee,
    Camera,
    Palette,
    PenLine
} from 'lucide-react';

/* Reusable Primitives */

const SectionTag = ({ icon: IconComp, label, color = "purple" }) => {
    const colorClasses = {
        indigo: "bg-indigo-500/10 border-indigo-500/20 text-indigo-300",
        rose: "bg-rose-500/10 border-rose-500/20 text-rose-300",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
        amber: "bg-amber-500/10 border-amber-500/20 text-amber-300",
        purple: "bg-purple-500/10 border-purple-500/20 text-purple-300"
    };
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border text-xs sm:text-sm font-semibold tracking-wide uppercase ${colorClasses[color]}`}>
            <IconComp className="w-4 h-4" /> {label}
        </div>
    );
};

const StatCard = ({ value, label, sub, accent = "purple" }) => {
    const colors = {
        indigo: "from-indigo-900/40 to-slate-900/80 border-indigo-500/30",
        emerald: "from-emerald-900/40 to-slate-900/80 border-emerald-500/30",
        purple: "from-purple-900/40 to-slate-900/80 border-purple-500/30",
        amber: "from-amber-900/40 to-slate-900/80 border-amber-500/30",
        rose: "from-rose-900/40 to-slate-900/80 border-rose-500/30"
    };
    return (
        <div className={`bg-gradient-to-br ${colors[accent]} border rounded-2xl p-5 sm:p-6 text-center h-full flex flex-col justify-center`}>
            <div className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-1">{value}</div>
            <div className="text-xs sm:text-sm font-semibold text-white/80">{label}</div>
            {sub && <div className="text-[10px] sm:text-xs text-white/50 mt-2">{sub}</div>}
        </div>
    );
};

const MiniCard = ({ icon: IconComp, title, sub, color = "purple" }) => {
    const ring = {
        indigo: "text-indigo-300",
        emerald: "text-emerald-300",
        purple: "text-purple-300",
        amber: "text-amber-300",
        rose: "text-rose-300"
    };
    return (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 h-full">
            <IconComp className={`w-6 h-6 mb-3 ${ring[color]}`} />
            <h3 className="text-white font-bold text-base sm:text-lg mb-1 leading-snug">{title}</h3>
            <p className="text-slate-400 text-sm leading-snug">{sub}</p>
        </div>
    );
};

const Check = ({ children }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-400" />
        <span className="leading-snug text-slate-200 text-sm">{children}</span>
    </li>
);

const SectionHead = ({ icon, tag, color, title, sub }) => (
    <div className="text-center mb-10">
        <SectionTag icon={icon} label={tag} color={color} />
        <h2 className="text-3xl sm:text-4xl font-black mb-3">{title}</h2>
        {sub && <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">{sub}</p>}
    </div>
);

/* Two-path growth: stay the same vs scale with us (cumulative posts over 6 months) */
const MONTHS = ["M1", "M2", "M3", "M4", "M5", "M6"];
const XS = [70, 174, 278, 382, 486, 590];
const ALONE = [30, 60, 90, 120, 150, 180];        // 30 / month, cumulative
const WITHUS = [240, 480, 720, 1200, 1920, 3120];  // 240 / month, then fan pages + clipping + YouTube from month 3
const yOf = (v) => 235 - v * 0.0609;

const GrowthCompare = () => {
    const [withUs, setWithUs] = useState(true);
    const data = withUs ? WITHUS : ALONE;
    const ghost = withUs ? ALONE : WITHUS;
    const stroke = withUs ? "#e879f9" : "#fb7185";
    const fillId = withUs ? "gWith" : "gAlone";
    const line = data.map((v, i) => `${XS[i]},${yOf(v).toFixed(1)}`).join(" ");
    const ghostLine = ghost.map((v, i) => `${XS[i]},${yOf(v).toFixed(1)}`).join(" ");
    const area = `M${XS[0]},${yOf(data[0]).toFixed(1)} ` + data.slice(1).map((v, i) => `L${XS[i + 1]},${yOf(v).toFixed(1)}`).join(" ") + ` L590,235 L70,235 Z`;
    const total = withUs ? "3,120" : "180";
    return (
        <div className="max-w-3xl mx-auto rounded-3xl border border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-slate-900/60 p-5 sm:p-8">
            <div className="flex flex-wrap justify-center gap-2 mb-6">
                <button onClick={() => setWithUs(false)} className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold border transition ${!withUs ? "bg-rose-500/25 border-rose-400/60 text-white" : "bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20"}`}>If you continue alone</button>
                <button onClick={() => setWithUs(true)} className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold border transition ${withUs ? "bg-purple-500/30 border-purple-400/60 text-white" : "bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20"}`}>With SocialSEO</button>
            </div>
            <div className="text-center mb-4">
                <div className="text-5xl sm:text-6xl font-black text-white">{total}</div>
                <div className="text-sm font-semibold" style={{ color: stroke }}>posts in 6 months</div>
            </div>
            <svg viewBox="0 0 640 270" className="w-full h-auto">
                <defs>
                    <linearGradient id="gWith" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#d946ef" stopOpacity="0.5" />
                        <stop offset="100%" stopColor="#a855f7" stopOpacity="0.03" />
                    </linearGradient>
                    <linearGradient id="gAlone" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fb7185" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#fb7185" stopOpacity="0.03" />
                    </linearGradient>
                </defs>
                {/* the other path, faint for contrast */}
                <polyline points={ghostLine} fill="none" stroke="#94a3b8" strokeOpacity="0.4" strokeWidth="2" strokeDasharray="5 5" />
                {withUs && (
                    <g>
                        <line x1="278" y1="40" x2="278" y2="235" stroke="#ffffff" strokeOpacity="0.12" />
                        <text x="284" y="54" fill="#ffffff" fontSize="11" opacity="0.55">Month 3: fan pages + clipping + YouTube</text>
                    </g>
                )}
                <path d={area} fill={`url(#${fillId})`} />
                <polyline points={line} fill="none" stroke={stroke} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                {data.map((v, i) => (
                    <circle key={i} cx={XS[i]} cy={yOf(v)} r="4" fill="#fff" stroke={stroke} strokeWidth="2" />
                ))}
                {MONTHS.map((m, i) => (
                    <text key={m} x={XS[i]} y="258" fill="#94a3b8" fontSize="12" textAnchor="middle">{m}</text>
                ))}
            </svg>
            <p className="text-center text-slate-300 text-sm leading-snug mt-4 max-w-xl mx-auto">
                {withUs
                    ? "240 posts a month from day one. From month 3, fan pages, clipping and YouTube compound it, while your effort stays the same one weekly sit-down."
                    : "At 30 posts a month on your own, the total barely moves. The dashed line is what we would be posting for you over the same six months."}
            </p>
        </div>
    );
};

/* Main Component */
export default function NidhiPersonalBrand() {

    useEffect(() => {
        document.title = "Nidhi | Content System Proposal";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-purple-500/30 pb-20 pt-24 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-purple-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Nidhi</span>
                </div>
                <div className="bg-purple-500/20 backdrop-blur-lg border border-purple-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-purple-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Proposal</span>
                </div>
            </header>

            <main className="max-w-[1120px] mx-auto px-4 space-y-20 sm:space-y-28">

                {/* HERO (no price) */}
                <section className="text-center pt-6 sm:pt-10">
                    <SectionTag icon={Rocket} label="Founder-Led Content System" color="purple" />
                    <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-200 to-pink-400 leading-tight pb-2">
                        You stay on camera.<br />We run everything else.
                    </h1>
                    <p className="text-base sm:text-xl md:text-2xl text-white/60 max-w-3xl mx-auto leading-relaxed mb-10">
                        A system that turns 60 videos a month into 240 posts across 4 platforms, growing your audience and Cuffs n Lashes at the same time.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 max-w-3xl mx-auto gap-3 sm:gap-4 mt-10">
                        <StatCard value="60" label="Videos / month" sub="recorded with you" accent="purple" />
                        <StatCard value="240" label="Posts / month" sub="across 4 platforms" accent="rose" />
                        <StatCard value="0 hrs" label="On editing or posting" sub="you just record" accent="emerald" />
                        <StatCard value="2-in-1" label="Creator + brand" sub="audience and Cuffs n Lashes" accent="amber" />
                    </div>
                </section>

                {/* YOUR EDGE / PILLARS */}
                <section>
                    <SectionHead
                        icon={Sparkles} tag="Your edge" color="purple"
                        title="Not a beauty page. A founder building out loud."
                        sub="This is not makeup tutorials. It is the story of a real person building a real cosmetics brand in public, the rarest and stickiest angle there is."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
                        <MiniCard icon={Eye} color="purple" title="Founder's journey" sub="The build-in-public story people follow for." />
                        <MiniCard icon={TrendingUp} color="rose" title="Business + entrepreneurship" sub="The wins, lessons, and decisions of building a brand." />
                        <MiniCard icon={ShoppingBag} color="amber" title="Cuffs n Lashes" sub="Your products, woven in, never a hard ad." />
                        <MiniCard icon={Camera} color="emerald" title="Day-to-day life" sub="The real, relatable moments that make people stay." />
                    </div>
                </section>

                {/* PAIN */}
                <section>
                    <SectionHead
                        icon={XCircle} tag="What's slowing you down" color="rose"
                        title="You're great on camera. The rest eats your time."
                        sub="Running a creator page and a cosmetics brand at once is two full jobs. The production is the part that stalls."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
                        <MiniCard icon={Calendar} color="rose" title="No consistent system" sub="Posting in bursts, then gaps. The algorithm punishes the gaps." />
                        <MiniCard icon={Workflow} color="rose" title="Editing + posting is a grind" sub="The hours that should go into product and recording go into the timeline." />
                        <MiniCard icon={Target} color="rose" title="Ideas under pressure" sub="Coming up with what to post, every day, on top of running a brand." />
                    </div>
                </section>

                {/* OUR SIDE (content strategy, no scripting) */}
                <section>
                    <SectionHead
                        icon={Workflow} tag="What we do" color="indigo"
                        title="The whole engine, run for you"
                        sub="You record the raw moments with us. Everything downstream is ours."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
                        <MiniCard icon={Target} color="indigo" title="Content strategy" sub="The monthly plan across your 4 pillars, you approve it." />
                        <MiniCard icon={PenLine} color="indigo" title="Writing + captions" sub="Hooks and copy in your real Hinglish voice." />
                        <MiniCard icon={Palette} color="indigo" title="Your y2k editing" sub="Pastel palette, fast cuts, captions, subtle SFX." />
                        <MiniCard icon={Camera} color="indigo" title="60 videos / month" sub="Repurposed into 240 posts across 4 platforms." />
                        <MiniCard icon={Calendar} color="indigo" title="Posting + management" sub="We run the pages day to day." />
                        <MiniCard icon={BarChart3} color="indigo" title="Weekly dashboard" sub="Reach, saves, profile visits, product DMs." />
                    </div>
                </section>

                {/* GROWTH GRAPH */}
                <section>
                    <SectionHead
                        icon={TrendingUp} tag="The volume you've never had" color="purple"
                        title="Two paths over the next 6 months"
                        sub="On your own at 30 a month, the total barely moves. With us you start at 240 a month, and from month 3 fan pages, clipping and YouTube long-form make it exponential. Tap between the two."
                    />
                    <GrowthCompare />
                </section>

                {/* YOUR SIDE (two things only) */}
                <section>
                    <SectionHead
                        icon={HeartHandshake} tag="Your side" color="emerald"
                        title="Two things, that is the whole ask"
                        sub="You stay on camera. We carry everything else and keep tuning it with you."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
                        <MiniCard icon={Camera} color="emerald" title="Record with us on Tuesdays" sub="One sit-down a week with Suresh to capture the content." />
                        <MiniCard icon={BarChart3} color="emerald" title="Check the weekly dashboard" sub="We read the numbers together and keep adjusting what works." />
                    </div>
                </section>

                {/* METRICS */}
                <section>
                    <SectionHead
                        icon={BarChart3} tag="How we measure it" color="purple"
                        title="Two numbers that matter"
                        sub="Reach matters here because discovery drives a brand. But reach alone is vanity, so we track demand too."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        <div className="rounded-2xl border border-purple-500/30 bg-purple-900/10 p-6 sm:p-8">
                            <div className="text-purple-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-3">Lead metric, weekly</div>
                            <h3 className="text-lg font-bold text-white mb-3">Reach + saves + profile visits</h3>
                            <p className="text-slate-400 text-sm leading-snug">New audiences finding you, saving your content, and clicking through to the founder and the brand behind it.</p>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-6 sm:p-8">
                            <div className="text-emerald-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-3">Lag metric, quarterly</div>
                            <h3 className="text-lg font-bold text-white mb-3">Audience growth + Cuffs n Lashes demand</h3>
                            <p className="text-slate-400 text-sm leading-snug">Follower growth plus product-interest DMs, link clicks, and inbound brand-collab and PR offers.</p>
                        </div>
                    </div>
                </section>

                {/* INVESTMENT (price lives here, not at the top) */}
                <section>
                    <SectionHead
                        icon={IndianRupee} tag="Investment" color="emerald"
                        title="One number, everything included"
                        sub="Every tool, every platform, your whole team. No add-ons, no surprises."
                    />
                    <div className="max-w-md mx-auto rounded-2xl border-2 border-purple-500/40 bg-purple-900/15 p-7 sm:p-9 text-center">
                        <div className="text-purple-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-4">Founder-Led Content System</div>
                        <div className="flex items-end justify-center gap-2 mb-2">
                            <span className="text-5xl font-black text-white">₹1,25,000</span>
                            <span className="text-slate-400 text-sm mb-1.5">/ month</span>
                        </div>
                        <p className="text-slate-400 text-sm mb-6">60 videos a month, fully done for you.</p>
                        <ul className="space-y-2.5 text-left">
                            <Check>60 videos a month, repurposed into 240 posts across 4 platforms</Check>
                            <Check>Content strategy, writing, editing, posting, management</Check>
                            <Check>A dedicated editor, strategist and manager</Check>
                            <Check>Fan pages, clipping and YouTube layer from month 3</Check>
                        </ul>
                    </div>
                </section>

                {/* CLOSING */}
                <section className="text-center pb-10">
                    <div className="max-w-2xl mx-auto bg-gradient-to-br from-purple-900/50 to-slate-900/80 border border-purple-500/30 rounded-3xl p-8 sm:p-10 md:p-14 shadow-2xl">
                        <Rocket className="w-9 h-9 sm:w-10 sm:h-10 text-purple-400 mx-auto mb-4" />
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 text-white">You stay on camera. We run the rest.</h2>
                        <p className="text-slate-300 text-base sm:text-lg mb-2">One system that grows your audience and your brand at the same time.</p>
                        <p className="text-slate-400 text-sm">Suresh Malani · SocialSEO LLP</p>
                    </div>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-8 border-t border-white/10">
                    <div className="font-bold text-white/70 mb-2">SocialSEO LLP</div>
                    <div className="mb-1">Delhi + Dubai</div>
                    <div className="mb-2">
                        <a href="mailto:suresh@socialseo.in" className="text-purple-300 hover:text-purple-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
